/**
 * @file PublicationsPage.js
 * @module src/js/pages/PublicationsPage
 * @description
 * **Renders the Publications page, allowing users to view, search, paginate, upload, edit, and delete publication entries.**
 * - Utilizes various components such as `CategoryPagination`, `NumberPagination`, `PagedTable`, `PublicationsUploadForm`, and `SearchBar`.
 * - Fetches publication data from the backend API and manages state for filtering, pagination, and user interactions.
 * - Special rule: if `Month = 99`, only the year will be used for `Date` display.
 *
 * @version
 * <p>
 *  2025-11-26: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-11-30: The "NEW" image added for news within 90 days. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-11-30: The "-99" in `item.Date` remains present after fetching. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useDeleteEntry. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2026-10-03: Category bar uses two rows so every category is visible without horizontal scrolling. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useUser } from "../contexts/UserProvider";
import { useTheme } from "../contexts/ThemeProvider";
import useFetchFromBackend from "../hooks/useFetchFromBackend";
import useDeleteEntry from "../hooks/useDeleteEntry";
import CategoryPagination from '../components/CategoryPagination';
import NumberPagination from '../components/NumberPagination';
import PagedTable from '../components/PagedTable';
import PublicationsUploadForm from '../components/PublicationsUploadForm';
import SearchBar from "../components/SearchBar";
import SpanARow from "../components/SpanARow";
import { ExtendedMarkdown } from "../components/UtilityComponents";
import newGif from '../../images/new.gif';

export default function PublicationsPage() {
    const categories = useMemo(() => ({
        acm_ieee_journal: 'ACM/IEEE Journal/Magazine Papers',
        other_journal: 'Other Journal/Magazine Papers',
        books: 'Books and Book Chapters',
        acm_ieee_conference: 'ACM/IEEE Conference Papers',
        other_conference: 'Other Conference Papers/Reports',
        us_patents: 'US Patents',
        taiwan_patents: 'Taiwan/R.O.C. Patents',
        technical: 'Technical Reports',
        dissertation: 'Dissertation',
        other_articles: 'Other Articles'
    }), []);

    const numPageButtons = 10;
    const numItemsPerPage = 10;

    const { pathname } = useLocation();
    const { uid } = useUser();
    const { theme } = useTheme();
    const fetchFromBackend = useFetchFromBackend();
    const [publications, setPublications] = useState([]);
    const [filteredPublications, setFilteredPublications] = useState([]);
    const [currentCategory, setCurrentCategory] = useState(Object.values(categories)[0]);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentFirstPage, setCurrentFirstPage] = useState(1);
    const [updatedID, setUpdatedID] = useState(-1);
    const [deleting, setDeleting] = useState(false);
    const [loading, setLoading] = useState(true);
    const headerRef = useRef(null);
    const deleteEntry = useDeleteEntry({ setData: setPublications, setDeleting });

    const publicationsOnCurrentPage = useMemo(() => {
        if (!filteredPublications || filteredPublications.length === 0) return [];
        const start = (currentPage - 1) * numItemsPerPage;
        const end = currentPage * numItemsPerPage;
        return filteredPublications.slice(start, end);
    }, [filteredPublications, currentPage]);

    useEffect(() => {
        async function fetchData() {
            const api = Object.keys(categories).find(key => categories[key] === currentCategory);
            const data = await fetchFromBackend({ request: `/api/publications/${api}` });
            setPublications(data || []);
            setLoading(false);
        }
        fetchData();
    }, [currentCategory, fetchFromBackend, categories]);
    
    const categoryPagination = (
        <CategoryPagination
            categories={Object.values(categories)}
            currentCategory={currentCategory}
            setCurrentCategory={setCurrentCategory}
            headerRef={headerRef}
            rows={2}
        />
    )

    const numberPagination = (
        <NumberPagination
            length={filteredPublications.length}
            numPageButtons={numPageButtons}
            numItemsPerPage={numItemsPerPage}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            currentFirstPage={currentFirstPage}
            setCurrentFirstPage={setCurrentFirstPage}
            currentCategory={currentCategory}
            headerRef={headerRef}
        />
    )

    // The reason for using useMemo here is to prevent unnecessary re-renders
    // You can find more details in the News.js component
    const filterKeys = useMemo(() => ['Date', 'Title'], []);
    const searchBar = (
        <SearchBar
            data={publications}
            setFilteredData={setFilteredPublications}
            setCurrentPage={setCurrentPage}
            setCurrentFirstPage={setCurrentFirstPage}
            currentCategory={currentCategory}
            filterKeys={filterKeys}
        />
    );

    const table = (
        <div className="table-wrapper">
            <table>
                <thead>
                    <tr>
                        <th className='hori-vert-center' width='15%'>Time</th>
                        <th className='hori-vert-center' colSpan={uid !== 'Guest' ? 2 : 1}>{currentCategory}</th>
                    </tr>
                </thead>
                <tbody>
                    <PublicationsUploadForm // For uploading new publications
                        categories={categories}
                        setData={setPublications}
                        setCurrentCategory={setCurrentCategory}
                    />
                    {filteredPublications.length === 0 && (
                        <SpanARow
                            colSpan={uid === 'Guest' ? 2 : 3}
                            text="No publications found"
                            loading={loading}
                        />
                    )}
                    {publicationsOnCurrentPage.map((item, i) => (
                        item.PublicationsID !== updatedID ? (
                            <tr key={i}>
                                <td className='hori-vert-center'>
                                    {item.Date.includes('-99') ?
                                        item.Date.split('-')[0] :
                                         item.Date
                                    }<br />
                                    {(item.Date.includes('-99') ?
                                        new Date(item.Date.split('-')[0], 0, 1) :
                                        new Date(item.Date)
                                    ) >= new Date(Date.now() - 90 * 24 * 60 * 60 * 1000) && (
                                        <img src={newGif} alt='New' style={{ marginLeft: '5px' }} />
                                    )}
                                </td>
                                <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>
                                    <ExtendedMarkdown>
                                        {item.Title}
                                    </ExtendedMarkdown>
                                </td>
                                {uid !== 'Guest' && (
                                    <td className='hori-vert-center' width='10%'>
                                        <button
                                            onClick={() => setUpdatedID(item.PublicationsID)}
                                            disabled={deleting}
                                            style={{ marginBottom: '15px' }}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => deleteEntry({
                                                request: `/api/publications/${item.Type}`,
                                                id: item.PublicationsID,
                                                from: pathname,
                                            })}
                                            disabled={deleting}
                                            style={{
                                                color: theme === 'dark' ? 'lightcoral' : 'red',
                                                outline: `2px solid ${theme === 'dark' ? 'lightcoral' : 'rgba(255, 0, 0, 0.1)'}`,
                                                outlineOffset: '-2px',
                                            }}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ) : (
                            <PublicationsUploadForm // For updating existing publications
                                key={i}
                                item={item}
                                categories={categories}
                                setData={setPublications}
                                setCurrentCategory={setCurrentCategory}
                                setUpdatedID={setUpdatedID}
                            />
                        )
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <PagedTable
            header="Publications"
            headerRef={headerRef}
            categoryPagination={categoryPagination}
            numberPagination={filteredPublications.length > numItemsPerPage ? numberPagination : null}
            table={table}
            searchBar={searchBar}
        />
    );
}
